// src/admin/components/POViewDialog.tsx
import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Button,
  Grid,
  Box,
  Typography,
  IconButton,
  Stack,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Divider,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  LocalShipping as LocalShippingIcon,
  Cancel as CancelIcon,
  AttachMoney as AttachMoneyIcon,
  Warehouse as WarehouseIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { PurchaseOrder, PurchaseOrderStatus, PO_STATUS_COLORS, PAYMENT_STATUS_COLORS, PurchaseOrderItem } from './PurchaseOrder';
import { PurchaseOrderService } from './purchaseOrderService';
import { PurchaseOrderReturnService } from './purchaseOrderReturnService';
import { WarehouseService } from '../warehouse/warehouseService';
import { Warehouse } from '../../../enums/product';
import { useCompany } from '../../../contexts/CompanyContext';
import { getCurrencySymbol } from '../../../util/productUtils';
import { useAuth } from '../../../contexts/AuthContext';
import POReturnDialog from './POReturnDialog';

interface POViewDialogProps {
  open: boolean;
  onClose: () => void;
  purchaseOrder: PurchaseOrder;
  onStatusUpdate: (poId: string, status: PurchaseOrderStatus) => void;
  onRefresh: () => void;
}

interface WarehouseSplit {
  warehouseId: string;
  quantity: number;
}

type ItemWarehouseAllocations = { [itemId: string]: WarehouseSplit[] };

// ── Confirmation dialog state shape ─────────────────────────────────────────
interface ConfirmDialogState {
  open: boolean;
  title: string;
  message: React.ReactNode;
  loading: boolean;
  onConfirm: () => Promise<void>;
}

const CONFIRM_DIALOG_CLOSED: ConfirmDialogState = {
  open: false,
  title: '',
  message: '',
  loading: false,
  onConfirm: async () => { },
};

// ── Error/info alert dialog state shape ─────────────────────────────────────
interface AlertDialogState {
  open: boolean;
  title: string;
  message: string;
  severity: 'error' | 'warning' | 'info';
}

const ALERT_DIALOG_CLOSED: AlertDialogState = {
  open: false,
  title: '',
  message: '',
  severity: 'error',
};

// ── Comma-separated amount formatter ─────────────────────────────────────────
// Formats a number to 2 decimal places with thousands separators.
// e.g. 1234567.5 → "1,234,567.50"
const formatAmount = (value: number): string =>
  value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ── Theme-aware chip style helper ─────────────────────────────────────────────
//
// In light mode: soft pastel tints (low-opacity fills).
// In dark mode:  richer semi-transparent fills + brighter text so the chip
//                is legible against dark paper backgrounds.
//
// Pass `isDark` (from useTheme) to switch between the two palettes.
// ─────────────────────────────────────────────────────────────────────────────
const getSoftChipSx = (color: string, isDark: boolean = false) => {
  type PaletteEntry = { bg: string; border: string; text: string; iconColor: string };

  const lightPalette: Record<string, PaletteEntry> = {
    warning: { bg: 'rgba(255,167,38,0.12)', border: 'rgba(255,167,38,0.4)', text: '#b76e00', iconColor: '#e68900' },
    info: { bg: 'rgba(41,182,246,0.12)', border: 'rgba(41,182,246,0.4)', text: '#0277bd', iconColor: '#039be5' },
    primary: { bg: 'rgba(33,150,243,0.12)', border: 'rgba(33,150,243,0.4)', text: '#1565c0', iconColor: '#1976d2' },
    success: { bg: 'rgba(102,187,106,0.15)', border: 'rgba(102,187,106,0.45)', text: '#2e7d32', iconColor: '#43a047' },
    error: { bg: 'rgba(239,83,80,0.12)', border: 'rgba(239,83,80,0.4)', text: '#c62828', iconColor: '#e53935' },
    secondary: { bg: 'rgba(171,71,188,0.10)', border: 'rgba(171,71,188,0.35)', text: '#6a1b9a', iconColor: '#8e24aa' },
    default: { bg: 'rgba(158,158,158,0.12)', border: 'rgba(158,158,158,0.4)', text: '#424242', iconColor: '#757575' },
  };

  const darkPalette: Record<string, PaletteEntry> = {
    warning: { bg: 'rgba(255,167,38,0.22)', border: 'rgba(255,167,38,0.55)', text: '#ffcc80', iconColor: '#ffa726' },
    info: { bg: 'rgba(41,182,246,0.20)', border: 'rgba(41,182,246,0.50)', text: '#81d4fa', iconColor: '#29b6f6' },
    primary: { bg: 'rgba(33,150,243,0.22)', border: 'rgba(33,150,243,0.50)', text: '#90caf9', iconColor: '#42a5f5' },
    success: { bg: 'rgba(102,187,106,0.22)', border: 'rgba(102,187,106,0.50)', text: '#a5d6a7', iconColor: '#66bb6a' },
    error: { bg: 'rgba(239,83,80,0.22)', border: 'rgba(239,83,80,0.50)', text: '#ef9a9a', iconColor: '#ef5350' },
    secondary: { bg: 'rgba(171,71,188,0.22)', border: 'rgba(171,71,188,0.50)', text: '#ce93d8', iconColor: '#ab47bc' },
    default: { bg: 'rgba(158,158,158,0.22)', border: 'rgba(158,158,158,0.50)', text: '#e0e0e0', iconColor: '#bdbdbd' },
  };

  const palette = isDark ? darkPalette : lightPalette;
  const p = palette[color] ?? palette.default;

  return {
    backgroundColor: p.bg,
    borderColor: p.border,
    color: p.text,
    fontWeight: 600,
    border: `1px solid ${p.border}`,
    '& .MuiChip-icon': { color: p.iconColor },
    '& .MuiChip-label': { color: p.text },
  };
};

// ── Map PO_STATUS_COLORS / PAYMENT_STATUS_COLORS values to getSoftChipSx keys ─
const poStatusToSoftColor = (status: PurchaseOrderStatus): string => {
  const raw = PO_STATUS_COLORS[status];
  const map: Record<string, string> = {
    default: 'default',
    warning: 'warning',
    info: 'info',
    primary: 'primary',
    success: 'success',
    error: 'error',
    secondary: 'secondary',
  };
  return map[raw] ?? 'default';
};

const paymentStatusToSoftColor = (status: string): string => {
  const raw = (PAYMENT_STATUS_COLORS as Record<string, string>)[status] ?? 'default';
  const map: Record<string, string> = {
    default: 'default',
    warning: 'warning',
    info: 'info',
    primary: 'primary',
    success: 'success',
    error: 'error',
    secondary: 'secondary',
  };
  return map[raw] ?? 'default';
};

const POViewDialog: React.FC<POViewDialogProps> = ({
  open, onClose, purchaseOrder: po, onStatusUpdate, onRefresh,
}) => {
  const { currentUser, hasPermission, isSuperAdmin } = useAuth();
  const { companyProfile } = useCompany();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // isDark drives all getSoftChipSx calls so chips are legible in both modes
  const isDark = theme.palette.mode === 'dark';

  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentLoading, setPaymentLoading] = useState(false);

  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [showWarehouseAllocation, setShowWarehouseAllocation] = useState(false);
  const [itemWarehouseAllocations, setItemWarehouseAllocations] = useState<ItemWarehouseAllocations>({});
  const [receivingLoading, setReceivingLoading] = useState(false);
  const [receivingError, setReceivingError] = useState('');
  const [statusUpdateLoading, setStatusUpdateLoading] = useState(false);

  const [returnDialogOpen, setReturnDialogOpen] = useState(false);
  const [poReturns, setPoReturns] = useState<any[]>([]);

  const canSubmit = isSuperAdmin || hasPermission('purchase_order.submit');
  const canApprove = isSuperAdmin || hasPermission('purchase_order.approve');
  const canOrder = isSuperAdmin || hasPermission('purchase_order.order');
  const canReceive = isSuperAdmin || hasPermission('purchase_order.receive');
  const canPayment = isSuperAdmin || hasPermission('purchase_order.payment');
  const canReturn = isSuperAdmin || hasPermission('inventory_return.manage');

  // ── Confirmation dialog ──────────────────────────────────────────────────
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState>(CONFIRM_DIALOG_CLOSED);

  const openConfirmDialog = (
    title: string,
    message: React.ReactNode,
    onConfirm: () => Promise<void>
  ) => {
    setConfirmDialog({ open: true, title, message, loading: false, onConfirm });
  };

  const handleConfirmDialogConfirm = async () => {
    setConfirmDialog(prev => ({ ...prev, loading: true }));
    try {
      await confirmDialog.onConfirm();
    } finally {
      setConfirmDialog(CONFIRM_DIALOG_CLOSED);
    }
  };

  const handleConfirmDialogCancel = () => {
    if (confirmDialog.loading) return;
    setConfirmDialog(CONFIRM_DIALOG_CLOSED);
  };

  // ── Alert (error/info) dialog ────────────────────────────────────────────
  const [alertDialog, setAlertDialog] = useState<AlertDialogState>(ALERT_DIALOG_CLOSED);

  const openAlertDialog = (
    title: string,
    message: string,
    severity: 'error' | 'warning' | 'info' = 'error'
  ) => {
    setAlertDialog({ open: true, title, message, severity });
  };

  const handleAlertDialogClose = () => {
    setAlertDialog(ALERT_DIALOG_CLOSED);
  };
  // ──────────────────────────────────────────────────────────────────────────

  const companyCurrency = companyProfile?.currency || 'LKR';
  const currencySymbol = getCurrencySymbol(companyCurrency);

  useEffect(() => {
    if (open && po.status === PurchaseOrderStatus.ORDERED) fetchWarehouses();
    if (open && po.status === PurchaseOrderStatus.RECEIVED) fetchPOReturns();
  }, [open, po.status]);

  const fetchPOReturns = async () => {
    try {
      const returns = await PurchaseOrderReturnService.getReturnsForPurchaseOrder(po.id!);
      setPoReturns(returns);
    } catch (err) { console.error(err); }
  };

  const fetchWarehouses = async () => {
    try {
      const data = await WarehouseService.getActiveWarehouses();
      setWarehouses(data);
      const defaultAllocations: ItemWarehouseAllocations = {};
      po.items.forEach(item => {
        if (item.id) {
          defaultAllocations[item.id] = [{
            warehouseId: data.length === 1 ? data[0].id! : '',
            quantity: item.quantity,
          }];
        }
      });
      setItemWarehouseAllocations(defaultAllocations);
    } catch (err) { console.error(err); }
  };

  const addWarehouseSplit = (itemId: string) => {
    setItemWarehouseAllocations(prev => ({
      ...prev,
      [itemId]: [...(prev[itemId] || []), { warehouseId: '', quantity: 0 }],
    }));
  };

  const removeWarehouseSplit = (itemId: string, splitIndex: number) => {
    setItemWarehouseAllocations(prev => {
      const splits = [...(prev[itemId] || [])];
      splits.splice(splitIndex, 1);
      return { ...prev, [itemId]: splits };
    });
  };

  const updateSplitWarehouse = (itemId: string, splitIndex: number, warehouseId: string) => {
    setItemWarehouseAllocations(prev => {
      const splits = [...(prev[itemId] || [])];
      splits[splitIndex] = { ...splits[splitIndex], warehouseId };
      return { ...prev, [itemId]: splits };
    });
  };

  const updateSplitQuantity = (itemId: string, splitIndex: number, quantity: number) => {
    setItemWarehouseAllocations(prev => {
      const splits = [...(prev[itemId] || [])];
      splits[splitIndex] = { ...splits[splitIndex], quantity };
      return { ...prev, [itemId]: splits };
    });
  };

  const allocatedQuantity = (itemId: string): number =>
    (itemWarehouseAllocations[itemId] || []).reduce((sum, s) => sum + (s.quantity || 0), 0);

  const remainingQuantity = (item: PurchaseOrderItem): number =>
    item.quantity - allocatedQuantity(item.id!);

  const handleStatusChange = async (status: PurchaseOrderStatus) => {
    if (status === PurchaseOrderStatus.RECEIVED) {
      setShowWarehouseAllocation(true);
      setReceivingError('');
      return;
    }
    openConfirmDialog(
      'Update Order Status',
      <>
        Are you sure you want to change the status to{' '}
        <strong>"{status}"</strong>?
      </>,
      async () => {
        try {
          setStatusUpdateLoading(true);
          await onStatusUpdate(po.id!, status);
          onClose();
        } catch (err: any) {
          openAlertDialog(
            'Status Update Failed',
            'Failed to update status: ' + (err.message || 'Unknown error')
          );
        } finally {
          setStatusUpdateLoading(false);
        }
      }
    );
  };

  const handleReceiveWithWarehouseAllocation = async () => {
    if (!currentUser) return;
    let validationError = '';
    for (const item of po.items) {
      if (!item.id) continue;
      const splits = itemWarehouseAllocations[item.id] || [];
      if (splits.length === 0) { validationError = `Please add at least one warehouse allocation for "${item.productName}".`; break; }
      for (const split of splits) {
        if (!split.warehouseId) { validationError = `Please select a warehouse for all rows of "${item.productName}".`; break; }
        if (!split.quantity || split.quantity <= 0) { validationError = `Please enter a valid quantity for all rows of "${item.productName}".`; break; }
      }
      if (validationError) break;
      const totalAllocated = allocatedQuantity(item.id);
      if (totalAllocated !== item.quantity) {
        validationError = `Total allocated quantity for "${item.productName}" (${totalAllocated}) must equal ordered quantity (${item.quantity}).`;
        break;
      }
    }
    if (validationError) { setReceivingError(validationError); return; }
    try {
      setReceivingLoading(true);
      setReceivingError('');
      const expandedItems: PurchaseOrderItem[] = [];
      for (const item of po.items) {
        const splits = item.id ? itemWarehouseAllocations[item.id] || [] : [];
        for (const split of splits) {
          expandedItems.push({
            ...item,
            quantity: split.quantity,
            totalPrice: item.unitPrice * split.quantity,
            receivedQuantity: split.quantity,
            warehouseId: split.warehouseId,
            warehouseName: warehouses.find(w => w.id === split.warehouseId)?.name,
          });
        }
      }
      await PurchaseOrderService.markPOAsReceivedWithInventoryUpdate(
        po.id!, expandedItems, currentUser.uid,
        currentUser.displayName || currentUser.email || 'Unknown'
      );
      setShowWarehouseAllocation(false);
      onRefresh();
      onClose();
    } catch (err: any) {
      setReceivingError(err.message || 'Failed to receive purchase order');
    } finally {
      setReceivingLoading(false);
    }
  };

  const handlePayment = async () => {
    const amount = parseFloat(paymentAmount);
    if (isNaN(amount) || amount <= 0) {
      openAlertDialog('Invalid Payment Amount', 'Please enter a valid payment amount.', 'warning');
      return;
    }
    const newPaidAmount = po.paidAmount + amount;
    if (newPaidAmount > po.totalAmount) {
      openAlertDialog('Payment Exceeds Total', 'Payment amount exceeds the total order amount.', 'warning');
      return;
    }
    try {
      setPaymentLoading(true);
      await PurchaseOrderService.updatePaymentStatus(po.id!, newPaidAmount);
      setPaymentAmount('');
      onRefresh();
      onClose();
    } catch (err: any) {
      openAlertDialog(
        'Payment Failed',
        'Failed to record payment: ' + (err.message || 'Unknown error')
      );
    } finally {
      setPaymentLoading(false);
    }
  };

  const outstandingAmount = po.totalAmount - po.paidAmount;

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{ sx: { width: { xs: '100%', sm: 700, md: 1000 } } }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>

          {/* Header */}
          <Box sx={{
            px: 3, py: 2,
            borderBottom: 1, borderColor: 'divider',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            flexShrink: 0,
          }}>
            <Box>
              <Typography variant="h6">Purchase Order Details</Typography>
              <Typography variant="body2" color="text.secondary">{po.poNumber}</Typography>
            </Box>
            <IconButton onClick={onClose} edge="end"><CloseIcon /></IconButton>
          </Box>

          {/* Scrollable Content */}
          <Box sx={{ flex: 1, overflow: 'auto', px: 3, py: 2 }}>
            <Grid container spacing={2}>

              {/* Status chips */}
              <Grid item xs={12}>
                <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                  <Chip
                    label={`Status: ${po.status}`}
                    size="small"
                    variant="outlined"
                    sx={getSoftChipSx(poStatusToSoftColor(po.status), isDark)}
                  />
                  <Chip
                    label={`Payment: ${po.paymentStatus}`}
                    size="small"
                    variant="outlined"
                    sx={getSoftChipSx(paymentStatusToSoftColor(po.paymentStatus), isDark)}
                  />
                  {po.stockUpdated && (
                    <Chip
                      label="Stock Updated"
                      size="small"
                      variant="outlined"
                      icon={<CheckCircleIcon />}
                      sx={getSoftChipSx('success', isDark)}
                    />
                  )}
                </Stack>
              </Grid>

              {/* Supplier Info */}
              <Grid item xs={12} sm={6}>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.paper', backgroundImage: 'none' }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Supplier Information</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>{po.supplierName}</Typography>
                  <Typography variant="body2" color="text.secondary">Email: {po.supplierEmail}</Typography>
                  <Typography variant="body2" color="text.secondary">Phone: {po.supplierPhone}</Typography>
                </Paper>
              </Grid>

              {/* Order Info */}
              <Grid item xs={12} sm={6}>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.paper', backgroundImage: 'none' }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Order Information</Typography>
                  <Typography variant="body2">Created: {po.createdAt.toLocaleDateString()} by {po.createdByName}</Typography>
                  {po.expectedDeliveryDate && (
                    <Typography variant="body2" color="text.secondary">
                      Expected Delivery: {po.expectedDeliveryDate.toLocaleDateString()}
                    </Typography>
                  )}
                  {po.actualDeliveryDate && <Typography variant="body2">Delivered: {po.actualDeliveryDate.toLocaleDateString()}</Typography>}
                  {po.approvedByName && <Typography variant="body2">Approved by: {po.approvedByName}</Typography>}
                  {po.receivedByName && <Typography variant="body2">Received by: {po.receivedByName}</Typography>}
                </Paper>
              </Grid>

              {/* Items Table */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Order Items</Typography>
                <Paper variant="outlined" sx={{ overflow: 'auto', bgcolor: 'background.paper', backgroundImage: 'none' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Product</TableCell>
                        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Variant</TableCell>
                        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>SKU</TableCell>
                        <TableCell align="right">Qty</TableCell>
                        <TableCell align="right">Unit Price</TableCell>
                        <TableCell align="right">Total</TableCell>
                        <TableCell align="right">Received</TableCell>
                        {po.status === PurchaseOrderStatus.RECEIVED && <TableCell>Warehouse</TableCell>}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {po.items.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <Stack direction="row" spacing={0.75} alignItems="center">
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>{item.productName}</Typography>
                              {item.itemType === 'raw_material' && (
                                <Chip label="Raw" size="small" color="secondary" sx={{ height: 18, fontSize: '0.65rem' }} />
                              )}
                            </Stack>
                            <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'block', sm: 'none' } }}>
                              {item.variantName || item.sku}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                            <Typography variant="caption" color="text.secondary">{item.itemType === 'raw_material' ? (item.unit || '-') : (item.variantName || '-')}</Typography>
                          </TableCell>
                          <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{item.sku}</TableCell>
                          <TableCell align="right">{item.quantity}</TableCell>
                          {/* formatAmount: comma-separated thousands, 2 decimal places */}
                          <TableCell align="right">{currencySymbol} {formatAmount(item.unitPrice)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>{currencySymbol} {formatAmount(item.totalPrice)}</TableCell>
                          <TableCell align="right">
                            <Chip
                              label={`${item.receivedQuantity}/${item.quantity}`}
                              size="small"
                              variant="outlined"
                              sx={getSoftChipSx(item.receivedQuantity === item.quantity ? 'success' : 'default', isDark)}
                            />
                          </TableCell>
                          {po.status === PurchaseOrderStatus.RECEIVED && (
                            <TableCell>
                              {item.warehouseName ? (
                                <Chip
                                  label={item.warehouseName}
                                  size="small"
                                  variant="outlined"
                                  icon={<WarehouseIcon />}
                                  sx={getSoftChipSx('primary', isDark)}
                                />
                              ) : (
                                <Typography variant="caption" color="text.secondary">Not assigned</Typography>
                              )}
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Paper>
              </Grid>

              {/* Warehouse Allocation */}
              {showWarehouseAllocation && (
                <Grid item xs={12}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      bgcolor: 'background.paper',
                      backgroundImage: 'none',
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                      <WarehouseIcon color="primary" />
                      <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                        Assign Warehouses for Received Items
                      </Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Assign one or more warehouses per item. Total allocated quantity must equal ordered quantity.
                    </Typography>
                    {receivingError && <Alert severity="error" sx={{ mb: 2 }}>{receivingError}</Alert>}

                    {po.items.map((item, itemIndex) => {
                      const itemId = item.id!;
                      const splits = itemWarehouseAllocations[itemId] || [];
                      const totalAllocated = allocatedQuantity(itemId);
                      const remaining = item.quantity - totalAllocated;
                      const isBalanced = remaining === 0;

                      return (
                        <Paper key={itemIndex} variant="outlined" sx={{
                          p: 2, mb: 2,
                          bgcolor: 'background.paper',
                          backgroundImage: 'none',
                          borderColor: isBalanced ? 'success.main' : 'warning.main', borderWidth: 1.5,
                        }}>
                          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ mb: 1.5 }} spacing={1}>
                            <Box>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                {item.productName}
                                {item.variantName && (
                                  <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                    ({item.variantName})
                                  </Typography>
                                )}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">SKU: {item.sku}</Typography>
                            </Box>
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                              <Chip
                                label={`Total: ${item.quantity}`}
                                size="small"
                                variant="outlined"
                                sx={getSoftChipSx('primary', isDark)}
                              />
                              <Chip
                                label={`Allocated: ${totalAllocated}`}
                                size="small"
                                variant="outlined"
                                sx={getSoftChipSx(isBalanced ? 'success' : 'warning', isDark)}
                              />
                              {!isBalanced && (
                                <Chip
                                  label={`Remaining: ${remaining}`}
                                  size="small"
                                  variant="outlined"
                                  sx={getSoftChipSx('error', isDark)}
                                />
                              )}
                            </Stack>
                          </Stack>

                          <Table size="small" sx={{ mb: 1 }}>
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ width: '55%' }}>Warehouse</TableCell>
                                <TableCell sx={{ width: '30%' }}>Quantity</TableCell>
                                <TableCell sx={{ width: '15%' }} />
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {splits.map((split, splitIndex) => (
                                <TableRow key={splitIndex}>
                                  <TableCell>
                                    <FormControl fullWidth size="small">
                                      <Select value={split.warehouseId} onChange={e => updateSplitWarehouse(itemId, splitIndex, e.target.value)} displayEmpty>
                                        <MenuItem value="" disabled>Select warehouse</MenuItem>
                                        {warehouses.map(warehouse => (
                                          <MenuItem key={warehouse.id} value={warehouse.id}>
                                            {warehouse.name} ({warehouse.code})
                                          </MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                  </TableCell>
                                  <TableCell>
                                    <TextField
                                      size="small" type="number"
                                      value={split.quantity}
                                      onChange={e => updateSplitQuantity(itemId, splitIndex, parseInt(e.target.value, 10) || 0)}
                                      inputProps={{ min: 1, max: item.quantity }}
                                      fullWidth
                                    />
                                  </TableCell>
                                  <TableCell align="center">
                                    {splits.length > 1 && (
                                      <IconButton size="small" color="error" onClick={() => removeWarehouseSplit(itemId, splitIndex)}>
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    )}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>

                          <Button size="small" startIcon={<AddIcon />} onClick={() => addWarehouseSplit(itemId)} disabled={isBalanced} variant="outlined" color="primary" sx={{ mt: 0.5 }}>
                            Add Warehouse
                          </Button>
                        </Paper>
                      );
                    })}

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 2 }} justifyContent="flex-end">
                      <Button onClick={() => { setShowWarehouseAllocation(false); setReceivingError(''); }} disabled={receivingLoading}>
                        Cancel
                      </Button>
                      <Button onClick={handleReceiveWithWarehouseAllocation} variant="contained" color="success"
                        startIcon={receivingLoading ? <CircularProgress size={16} /> : <LocalShippingIcon />} disabled={receivingLoading}>
                        {receivingLoading ? 'Receiving...' : 'Confirm Receipt & Update Stock'}
                      </Button>
                    </Stack>
                  </Paper>
                </Grid>
              )}

              {/* Financial Summary */}
              <Grid item xs={12} sm={6}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    bgcolor: 'background.paper',
                    backgroundImage: 'none',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Financial Summary</Typography>
                  <Stack spacing={1}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">Subtotal:</Typography>
                      <Typography variant="body2">{currencySymbol} {formatAmount(po.subtotal)}</Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">Tax ({po.taxRate}%):</Typography>
                      <Typography variant="body2">{currencySymbol} {formatAmount(po.taxAmount)}</Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">Shipping:</Typography>
                      <Typography variant="body2">{currencySymbol} {formatAmount(po.shippingCost)}</Typography>
                    </Stack>
                    <Divider />
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Total:</Typography>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{currencySymbol} {formatAmount(po.totalAmount)}</Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">Paid:</Typography>
                      <Typography variant="body2">{currencySymbol} {formatAmount(po.paidAmount)}</Typography>
                    </Stack>
                    {outstandingAmount > 0 && (
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">Outstanding:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{currencySymbol} {formatAmount(outstandingAmount)}</Typography>
                      </Stack>
                    )}
                  </Stack>
                </Paper>
              </Grid>

              {/* Record Payment */}
              {outstandingAmount > 0 && canPayment && (
                <Grid item xs={12} sm={6}>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.paper', backgroundImage: 'none' }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Record Payment</Typography>
                    <Stack direction="row" spacing={1}>
                      <TextField
                        size="small" type="number" label="Payment Amount"
                        value={paymentAmount}
                        onChange={(e) => setPaymentAmount(e.target.value)}
                        inputProps={{ min: 0, max: outstandingAmount, step: 0.01 }}
                        fullWidth
                      />
                      <Button variant="contained" onClick={handlePayment} disabled={paymentLoading || !paymentAmount} startIcon={<AttachMoneyIcon />}>
                        Record
                      </Button>
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Maximum: {currencySymbol} {formatAmount(outstandingAmount)}
                    </Typography>
                  </Paper>
                </Grid>
              )}

              {/* Notes */}
              {po.notes && (
                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.paper', backgroundImage: 'none' }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Notes</Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{po.notes}</Typography>
                  </Paper>
                </Grid>
              )}

              {/* Internal Notes — theme-aware warning tint instead of warning.lighter */}
              {po.internalNotes && (
                <Grid item xs={12}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      backgroundImage: 'none',
                      bgcolor: isDark ? 'rgba(255,167,38,0.08)' : 'rgba(255,167,38,0.06)',
                      borderColor: isDark ? 'rgba(255,167,38,0.35)' : 'rgba(255,167,38,0.30)',
                    }}
                  >
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Internal Notes</Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{po.internalNotes}</Typography>
                  </Paper>
                </Grid>
              )}

              {/* Returns */}
              {poReturns.length > 0 && (
                <Grid item xs={12}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      overflow: 'auto',
                      bgcolor: 'background.paper',
                      backgroundImage: 'none',
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Returns for this Purchase Order</Typography>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Return Number</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell align="right">Amount</TableCell>
                          <TableCell align="right">Refund</TableCell>
                          <TableCell>Date</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {poReturns.map((ret: any) => (
                          <TableRow key={ret.id}>
                            <TableCell>{ret.returnNumber}</TableCell>
                            <TableCell>
                              <Chip
                                label={ret.status}
                                size="small"
                                variant="outlined"
                                sx={getSoftChipSx(ret.status === 'completed' ? 'success' : 'warning', isDark)}
                              />
                            </TableCell>
                            {/* Comma-separated amounts, plain text — no colour */}
                            <TableCell align="right">{currencySymbol} {formatAmount(ret.totalAmount)}</TableCell>
                            <TableCell align="right">{ret.refundIssued ? `${currencySymbol} ${formatAmount(ret.refundAmount)}` : '-'}</TableCell>
                            <TableCell>{ret.createdAt.toLocaleDateString()}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Paper>
                </Grid>
              )}

            </Grid>
          </Box>

          {/* Footer */}
          <Divider />
          <Box sx={{ px: 3, py: 2, flexShrink: 0 }}>
            {!showWarehouseAllocation && (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap">

                {canSubmit && po.status === PurchaseOrderStatus.DRAFT && (
                  <Button onClick={() => handleStatusChange(PurchaseOrderStatus.PENDING)}
                    variant="outlined" color="warning" disabled={statusUpdateLoading} size="small">
                    Submit for Approval
                  </Button>
                )}

                {canApprove && po.status === PurchaseOrderStatus.PENDING && (
                  <>
                    <Button onClick={() => handleStatusChange(PurchaseOrderStatus.APPROVED)}
                      variant="outlined" color="success" startIcon={<CheckCircleIcon />}
                      disabled={statusUpdateLoading} size="small">
                      Approve
                    </Button>
                    <Button onClick={() => handleStatusChange(PurchaseOrderStatus.CANCELLED)}
                      variant="outlined" color="error" startIcon={<CancelIcon />}
                      disabled={statusUpdateLoading} size="small">
                      Cancel
                    </Button>
                  </>
                )}

                {canOrder && po.status === PurchaseOrderStatus.APPROVED && (
                  <Button onClick={() => handleStatusChange(PurchaseOrderStatus.ORDERED)}
                    variant="outlined" color="primary" disabled={statusUpdateLoading} size="small">
                    Mark as Ordered
                  </Button>
                )}

                {canReceive && po.status === PurchaseOrderStatus.ORDERED && !po.stockUpdated && (
                  <Button onClick={() => handleStatusChange(PurchaseOrderStatus.RECEIVED)}
                    variant="outlined" color="success" startIcon={<LocalShippingIcon />}
                    disabled={statusUpdateLoading} size="small">
                    Mark as Received
                  </Button>
                )}

                {canReturn && po.status === PurchaseOrderStatus.RECEIVED && (
                  <Button onClick={() => setReturnDialogOpen(true)}
                    variant="outlined" color="error" startIcon={<CancelIcon />} size="small">
                    Create Return
                  </Button>
                )}

                <Box sx={{ flex: 1 }} />
                <Button onClick={onClose} variant="contained" size="small">Close</Button>
              </Stack>
            )}
          </Box>
        </Box>
      </Drawer>

      <POReturnDialog
        open={returnDialogOpen}
        onClose={() => setReturnDialogOpen(false)}
        onSuccess={() => { setReturnDialogOpen(false); fetchPOReturns(); onRefresh(); }}
        purchaseOrder={po}
      />

      {/* ── Status Change Confirmation Dialog ───────────────────────────────── */}
      <Dialog open={confirmDialog.open} onClose={handleConfirmDialogCancel} maxWidth="xs" fullWidth>
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            {confirmDialog.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleConfirmDialogCancel} disabled={confirmDialog.loading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleConfirmDialogConfirm}
            disabled={confirmDialog.loading}
            startIcon={confirmDialog.loading ? <CircularProgress size={16} /> : <CheckCircleIcon />}
          >
            {confirmDialog.loading ? 'Updating...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Alert / Error Dialog ────────────────────────────────────────────── */}
      <Dialog open={alertDialog.open} onClose={handleAlertDialogClose} maxWidth="xs" fullWidth>
        <DialogTitle>{alertDialog.title}</DialogTitle>
        <DialogContent>
          <Alert severity={alertDialog.severity} sx={{ mt: 1 }}>
            {alertDialog.message}
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="contained" onClick={handleAlertDialogClose}>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default POViewDialog;